/**
 * Document Library (Stage 10 "Approved document library") distribution-state gate,
 * extracted to a dependency-free module so it is unit-testable without mounting
 * DocumentLibraryView.jsx's antd / AppContext / MSAL surface — mirrors the extraction
 * pattern already used for utils/evidenceAccess.js (see
 * .claude/lessons/workspace-active-step-persist-desync.md for why the full component
 * isn't a viable test harness).
 *
 * Backlog #20 / workflow-doc slide 14 / PRD §4.9. User ruling: "it is a roles based
 * distribution, more towards accessibility (publish and unpublish) so that the SPOC can
 * review it." This is a UX-only gate — the real enforcement is the backend's
 * `POST /projects/:id/approved-document-library/publish-state` ABAC check
 * (Commandment III/V); never trust this module alone to hide the content.
 */
import { Role } from '../constants/roles';

/**
 * @param {{ role?: string }|null|undefined} user
 * @param {{ published?: boolean, visibleToRoles?: string[], publishedAt?: string|null,
 *   publishedBy?: string|null, history?: Array<object> }|null|undefined} pkg
 *   `activeProject.profile.approvedDocumentLibraryPackage`
 */
export function getDocumentLibraryDistributionState(user, pkg) {
    const isPublished = !!pkg?.published;
    const visibleToRoles = Array.isArray(pkg?.visibleToRoles) ? pkg.visibleToRoles : [];
    // Newest first for display; does not mutate the append-only source array.
    const historyEntries = Array.isArray(pkg?.history) ? [...pkg.history].reverse() : [];
    // Mirrors OrganizationalSetup.jsx's `user?.role === UserRole.X` gate (a sibling Stage
    // 9/10 screen) rather than inventing a new pattern — LA/platform_admin only.
    const canManagePublishState = user?.role === Role.LEAD_ADVISOR || user?.role === Role.ADMIN;
    // Publishing is a Lead Advisor act (PRD s2.2): the API answers platform_admin 403
    // PLATFORM_ADMIN_APPROVAL_DENIED. Unpublish stays open to admin (revert, not approval).
    const canPublish = user?.role === Role.LEAD_ADVISOR;
    const canToggleDistribution = isPublished ? canManagePublishState : canPublish;
    const isCompanySpoc = user?.role === Role.COMPANY_SPOC;
    // Ruling: published AND this SPOC's role is in the advisor-chosen visibility list.
    const spocCanSeeLibrary = isPublished && visibleToRoles.includes('company_spoc');

    return {
        isPublished,
        visibleToRoles,
        historyEntries,
        canManagePublishState,
        canPublish,
        canToggleDistribution,
        isCompanySpoc,
        spocCanSeeLibrary,
    };
}


/**
 * Stage 10 gate mirror (Commandment V; the API's `isInternalAuditSignedOff` is the authority and answers 403
 * STAGE_PREREQUISITE_NOT_MET): publishing needs the Stage 9 report signed off by the Company SPOC, i.e.
 * `signOffStatus === 'approved'` AND the `signedOffAt` / `signedOffBy` stamp. UX only.
 * @param {{ internalAuditPackage?: { auditReport?: object } }|null|undefined} profile `activeProject.profile`
 */
export function isInternalAuditSignedOff(profile) {
    const r = profile?.internalAuditPackage?.auditReport;
    return !!r && r.signOffStatus === 'approved'
        && typeof r.signedOffAt === 'string' && !!r.signedOffAt
        && typeof r.signedOffBy === 'string' && !!r.signedOffBy;
}

export const INTERNAL_AUDIT_SIGN_OFF_REQUIRED_MESSAGE =
    'Publishing is locked until the Company SPOC signs off the Stage 9 internal audit report and action plan (Internal Audit > Audit Report).';
